package com.kinoshazam.uiKit.components.textField.compact

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.components.image.ImageUiKitState
import com.kinoshazam.uiKit.components.textField.TextFieldState
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.toDrawableResource
import com.kinoshazam.uiKit.extension.applyClickableIfNeeded
import com.kinoshazam.uiKit.extension.thenIf
import com.kinoshazam.uiKit.theme.HardLarge
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.close
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.InternalResourceApi
import org.jetbrains.compose.resources.painterResource

@Composable
fun CompactTextField(
    state: TextFieldState,
    modifier: Modifier = Modifier,
    keyboardActions: KeyboardActions = KeyboardActions(),
    focusRequester: FocusRequester? = null,
    onValueChanged: ((String) -> Unit)? = null,
    onClearIconClick: (() -> Unit)? = null,
    onRightIconClick: ((Any?) -> Unit)? = null,
    onLeftIconClick: ((Any?) -> Unit)? = null,
    onFieldClick: ((Any?) -> Unit)? = null,
) = Column(modifier) {
    state.hint?.let { hint ->
        TextLabel(
            modifier = Modifier.padding(start = 4.dp, bottom = 4.dp, end = 4.dp),
            state = hint,
        )
    }
    TextFieldContent(
        state = state,
        keyboardActions = keyboardActions,
        focusRequester = focusRequester,
        onValueChanged = onValueChanged,
        onClearIconClick = onClearIconClick,
        onRightIconClick = onRightIconClick,
        onLeftIconClick = onLeftIconClick,
        onFieldClick = onFieldClick,
    )

    state.label?.let { label ->
        TextLabel(
            modifier = Modifier.padding(start = 4.dp, top = 4.dp, end = 4.dp),
            state = label
        )
    }
}

@Composable
private fun TextFieldContent(
    state: TextFieldState,
    keyboardActions: KeyboardActions,
    focusRequester: FocusRequester?,
    onValueChanged: ((String) -> Unit)?,
    onClearIconClick: (() -> Unit)?,
    onRightIconClick: ((Any?) -> Unit)?,
    onLeftIconClick: ((Any?) -> Unit)?,
    onFieldClick: ((Any?) -> Unit)?,
) {
    Row(
        verticalAlignment = if (state.isSingleLine) Alignment.CenterVertically else Alignment.Top,
        modifier = Modifier
            .fillMaxWidth()
            .thenIf(
                with(Modifier) {
                    background(
                        state.containerColor ?: UiKitTheme.colors.color_4,
                        RoundedCornerShape(if (state.isSingleLine) 6.dp else 16.dp),
                    )
                }, true
            )
//            .applyClickableIfNeeded(isClickable = onFieldClick != null && state.isClickable) {
//                onFieldClick?.invoke(state.payload)
//            },
    ) {
        state.leftIcon?.LeadingIcon(
            onClick = onLeftIconClick,
        )
        Normal(
            state = state,
            onValueChanged = onValueChanged,
            keyboardActions = keyboardActions,
            focusRequester = focusRequester,
        )
        RightIconsRowContent(state, onClearIconClick, onRightIconClick)
    }

}

@Composable
private fun RowScope.Normal(
    state: TextFieldState,
    keyboardActions: KeyboardActions,
    focusRequester: FocusRequester?,
    onValueChanged: ((String) -> Unit)?,
) {
    var textValue by remember { mutableStateOf(TextFieldValue("")) }
    val incoming = state.textValue.orEmpty()
    if (textValue.text != incoming) {
        textValue = textValue.withTextKeepingCursor(incoming)
    }
    when (state.fieldType) {
        TextFieldState.FieldType.Normal -> {
            BasicTextField(
                value = textValue.copy(state.textValue.orEmpty()),
                onValueChange = { newValue ->
                    val newTextValue = state.inputFilter.filter(newValue)
                    val isTextChanged = newTextValue.text != textValue.text
                    textValue = newTextValue
                    if (onValueChanged != null && isTextChanged) {
                        onValueChanged(textValue.text)
                    }
                },
                singleLine = state.isSingleLine,
                minLines = if (state.isSingleLine) 1 else state.minLines,
                maxLines = if (state.isSingleLine) 1 else state.maxLines,
                keyboardOptions = KeyboardOptions(
                    keyboardType = state.inputType,
                    imeAction = state.imeAction,
                ),
                visualTransformation = state.visualTransformation,
                textStyle = if (state.showError) {
                    Medium.copy(color = UiKitTheme.colors.error)
                } else {
                    Medium.copy(color = UiKitTheme.colors.color_2)
                },
                cursorBrush = SolidColor(UiKitTheme.colors.color_1),
                enabled = !state.isClickable && state.isEnabled,
                keyboardActions = keyboardActions,
                modifier = Modifier
                    .weight(1f)
                    .padding(12.dp)
                    .then(
                        if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier
                    ),
                decorationBox = { innerTextField ->
                    Box(
                        modifier = Modifier
                            .fillMaxWidth(),
                    ) {
                        if (state.textValue.isNullOrEmpty() && state.placeholder != null) {
                            TextLabel(
                                state = state.placeholder,
                            )
                        }
                        innerTextField()
                    }
                },
            )
        }

        TextFieldState.FieldType.Outlined -> {
            DisableSelection {
                OutlinedTextField(
                    modifier = Modifier,
                    value = textValue.copy(state.textValue.orEmpty()),
                    onValueChange = { newValue ->
                        val newTextValue = state.inputFilter.filter(newValue)
                        val isTextChanged = newTextValue.text != textValue.text
                        textValue = newTextValue
                        if (onValueChanged != null && isTextChanged) {
                            onValueChanged(textValue.text)
                        }
                    },
                    singleLine = state.isSingleLine,
                    minLines = if (state.isSingleLine) 1 else state.minLines,
                    maxLines = if (state.isSingleLine) 1 else state.maxLines,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = state.inputType,
                        imeAction = state.imeAction,
                    ),
                    visualTransformation = state.visualTransformation,
                    textStyle = if (state.showError) {
                        HardLarge.copy(color = UiKitTheme.colors.error)
                    } else {
                        HardLarge.copy(color = UiKitTheme.colors.color_2)
                    },
                    enabled = !state.isClickable && state.isEnabled,
                    keyboardActions = keyboardActions,
                    colors = OutlinedTextFieldDefaults.colors(
                        cursorColor = UiKitTheme.colors.transparent,
                        focusedBorderColor = if (state.showError) {
                            UiKitTheme.colors.error
                        } else {
                            UiKitTheme.colors.color_1
                        },
                        unfocusedBorderColor = if (state.showError) {
                            UiKitTheme.colors.error
                        } else {
                            UiKitTheme.colors.color_6
                        },
                    )
                )
            }
        }
    }

}

private fun TextFieldValue.withTextKeepingCursor(text: String): TextFieldValue {
    val atEnd = selection.end >= this.text.length
    val cursor = if (atEnd) text.length else selection.end.coerceIn(0, text.length)
    return TextFieldValue(text = text, selection = TextRange(cursor))
}

@OptIn(ExperimentalResourceApi::class, InternalResourceApi::class)
@Composable
private fun RightIconsRowContent(
    state: TextFieldState,
    onClearIconClick: (() -> Unit)?,
    onRightIconClick: ((Any?) -> Unit)?,
) = Row(
    modifier = Modifier.padding(
        horizontal = 12.dp,
        vertical = if (state.isSingleLine) 0.dp else 12.dp,
    ),
    horizontalArrangement = Arrangement.spacedBy(16.dp),
) {
    if (state.hasProgress) {
        CircularProgressIndicator(
            modifier = Modifier.size(18.dp),
            color = UiKitTheme.colors.color_1,
            strokeWidth = 1.dp,
        )
    }
    if (state.hasClearButton && !state.textValue.isNullOrEmpty()) {
        Image(
            painter = painterResource(Res.drawable.close),
            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
            contentDescription = null,
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .clickable {
                    onClearIconClick?.invoke()
                }
        )
    }
    state.rightIcon?.let { iconState ->
        Image(
            painter = painterResource(DrawableResource(state.rightIcon.url.orEmpty(), setOf())),
            colorFilter = state.rightIcon.tintColor,
            contentDescription = state.rightIcon.contentDescription,
            modifier = Modifier
                .size(24.dp)
                .clip(CircleShape)
                .align(Alignment.CenterVertically)
                .applyClickableIfNeeded(isClickable = onRightIconClick != null) {
                    onRightIconClick?.invoke(state.payload)
                },
        )
    }
}

@OptIn(ExperimentalResourceApi::class, InternalResourceApi::class)
@Composable
private fun ImageUiKitState.LeadingIcon(
    onClick: ((Any?) -> Unit)?,
) = Image(
    painter = painterResource(url.orEmpty().toDrawableResource()),
    colorFilter = tintColor,
    contentDescription = contentDescription,
    modifier = Modifier
        .padding(start = 16.dp, end = 8.dp, top = 12.dp, bottom = 12.dp)
        .size(24.dp)
        .clip(CircleShape)
        .applyClickableIfNeeded(isClickable = onClick != null) {
            onClick?.invoke("Pass any info")
        },
)